

// src/Navbar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { auth, googleProvider, db } from "./firebase";
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  GoogleAuthProvider,
  signOut,
  setPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail
} from "firebase/auth";
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, setDoc } from "firebase/firestore";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import "./Navbar.css";

export default function Navbar({ user: propUser, globalCart, setIsCartOpen, isLoginOpen, setIsLoginOpen }) {
  // Use propUser OR fallback to sessionStorage for immediate UI update
  const [user, setUser] = useState(() => {
    if (propUser) return propUser;
    const stored = sessionStorage.getItem("userData");
    return stored ? JSON.parse(stored) : null;
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // const [isLoginOpen, setIsLoginOpen] = useState(false); // MOVED TO PROPS
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [view, setView] = useState(""); // Add this state for view management
  const [showGoogleInfo, setShowGoogleInfo] = useState(false);
  const [isAutoRedirecting, setIsAutoRedirecting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (propUser) {
      setUser(propUser);
    } else {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser) {
          const stored = sessionStorage.getItem("userData");
          if (stored) {
            setUser(JSON.parse(stored));
          } else {
            setUser(firebaseUser);
          }
        } else {
          setUser(null);
        }
      });
      return () => unsubscribe();
    }
  }, [propUser]);

  // Auto-redirection logic with debounce
  useEffect(() => {
    if (email.toLowerCase().endsWith("@nabi.health") && email.indexOf('@') > 0 && !isAutoRedirecting && isLoginOpen) {
      const timer = setTimeout(async () => {
        setIsAutoRedirecting(true);
        const authorized = await checkUserAuthorization(email);
        if (!authorized) {
          setIsAutoRedirecting(false);
          alert("❌ Access Denied: Your @nabi.health email is not authorized. Please contact admin.");
          return;
        }
        setShowGoogleInfo(true);
        setTimeout(() => {
          setShowGoogleInfo(false);
          setIsAutoRedirecting(false);
          handleSocialLogin(googleProvider);
        }, 2500);
      }, 1000); // 1-second debounce

      return () => clearTimeout(timer);
    }
  }, [email, isLoginOpen]); // Also depend on isLoginOpen to ensure it only runs when modal is open


  // NEW FUNCTION: Verify if user is authorized (Only for @nabi.health)
  const checkUserAuthorization = async (email) => {
    if (!email) return false;

    // Only apply restriction to @nabi.health domain
    if (email.toLowerCase().endsWith("@nabi.health")) {
      try {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        return methods.length > 0;
      } catch (error) {
        console.error("Authorization check failed:", error);
        return false;
      }
    }

    // All other domains are allowed freely
    return true;
  };

  const handleSocialLogin = async (provider) => {
    try {
      await setPersistence(auth, browserSessionPersistence);
      const freshProvider = new GoogleAuthProvider();
      freshProvider.setCustomParameters({
        prompt: "select_account"
      });

      const result = await signInWithPopup(auth, freshProvider);
      const user = result.user;

      // Sync Firestore: Ensure we don't create duplicate records and link pre-provisioned therapists
      const usersRef = collection(db, "users");
      const qUid = query(usersRef, where("uid", "==", user.uid));
      const qEmail = query(usersRef, where("email", "==", user.email));

      const [snapUid, snapEmail] = await Promise.all([getDocs(qUid), getDocs(qEmail)]);

      if (snapUid.empty && snapEmail.empty) {
        // New user entirely
        await addDoc(usersRef, {
          uid: user.uid,
          email: user.email,
          name: user.displayName || "New User",
          role: "user",
          emailVerified: user.emailVerified,
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp()
        });
      } else if (snapUid.empty && !snapEmail.empty) {
        // User was pre-provisioned (e.g. as a therapist) but has no UID yet
        const docId = snapEmail.docs[0].id;
        await setDoc(doc(db, "users", docId), {
          uid: user.uid,
          photoURL: user.photoURL || "",
          emailVerified: user.emailVerified
        }, { merge: true });
      }

      setIsLoginOpen(false);

    } catch (error) {
      if (error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-closed-by-user') {
        console.log("Popup was closed by user");
        return; // Silently ignore, don't reload so they don't lose state
      }
        alert("Login failed: " + error.message);
        setIsLoginOpen(false); // Only close modal on actual errors
      }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();

    try {
      await setPersistence(auth, browserSessionPersistence);

      // 🛑 Intercept @nabi.health emails manually submitted and force Google Auth
      if (email.toLowerCase().endsWith("@nabi.health")) {
        return handleSocialLogin(googleProvider);
      }

      // 🔐 STEP 1: Verify user is authorized (Google Console or Firebase Auth)
      const isAuthorized = await checkUserAuthorization(email);

      if (!isAuthorized) {
        alert("❌ Access Denied: You are not an authorized user. Please contact admin.");
        return;
      }

      if (isSignUpMode) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userData = {
          uid: user.uid,
          email: user.email,
          password: password,
          // name: user.displayName || "New User",
          name: user.displayName || email.split('@')[0],
          role: "user",
          emailVerified: user.emailVerified,
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp()
        };

        console.log(userData, "user handle email auth created details");
        await addDoc(collection(db, "users"), userData);
        setUser(userData);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        setUser(user);
        console.log(user, "user credentials");
      }
      setIsLoginOpen(false);
      setEmail("");
      setPassword("");
    } catch (error) {
      // Handle Provider Sync issues
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/email-already-in-use') {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.includes('google.com')) {
            if (window.confirm("This email is already associated with a Google account (Workspace). Would you like to log in with Google instead?")) {
              handleSocialLogin(googleProvider);
              return;
            }
          }
        } catch (syncErr) {
          console.warn("Provider sync check failed:", syncErr);
        }
      }
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found') {
        errorMessage = "No account found with this email. Please switch to 'Sign Up' to create one!";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Incorrect password. Please try again.";
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = "Incorrect email or password. Please try again or sign up.";
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = "An account already exists with this email. Please switch to 'Log In'.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Your password is too weak. It must be at least 6 characters long.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Please enter a valid email address.";
      }

      alert(errorMessage);
    }
    finally {
      setIsLoginOpen(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      alert("Please enter your email address first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert("Password reset email sent! Please check your inbox.");
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        alert("No account found with this email.");
      } else if (error.code === 'auth/invalid-email') {
        alert("Please enter a valid email address.");
      } else {
        alert("Failed to send password reset email: " + error.message);
      }
    }
  };

  const handleLogoutUser = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    console.log("Logout clicked");
    try {
      await signOut(auth);
      setUser(null);
      setIsMenuOpen(false);
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
      alert("Logout failed: " + err.message);
    }
  };

  // Check if user is admin
  const isAdmin = user?.email === "tech@nabi.health";

  if (location.pathname === "/verify") return null;

  return (
    <>
      {/* LOGIN MODAL */}
      <AnimatePresence>
        {isLoginOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="navbar-modal-overlay">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="navbar-modal-content">
              <button onClick={() => setIsLoginOpen(false)} className="navbar-modal-close">✕</button>
              <h1 className="navbar-modal-title">{isSignUpMode ? "Sign Up" : "Log In"}</h1>
              <p className="navbar-modal-sub">
                {isSignUpMode ? "Already a member?" : "New to Nabi?"}{" "}
                <span style={{ color: '#F07167', cursor: 'pointer', fontWeight: 'normal' }} onClick={() => setIsSignUpMode(!isSignUpMode)}>
                   {isSignUpMode ? "Log In" : "Sign Up"}
                </span>
              </p>

              <AnimatePresence>
                {showGoogleInfo && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="navbar-modal-google-info"
                  >
                    @nabi.health email detected. Redirecting to Google Sign-in...
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleEmailAuth} className="navbar-modal-form">
                <motion.input
                  type="email"
                  placeholder="Email"
                  className={`navbar-modal-input ${isAutoRedirecting ? 'navbar-modal-input-loading' : ''}`}
                  animate={isAutoRedirecting ? { opacity: [0.6, 1, 0.6] } : {}}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  disabled={isAutoRedirecting}
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  required
                />
                <motion.input
                  type="password"
                  placeholder="Password"
                  className={`navbar-modal-input ${isAutoRedirecting ? 'navbar-modal-input-loading' : ''}`}
                  animate={isAutoRedirecting ? { opacity: [0.6, 1, 0.6] } : {}}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  disabled={isAutoRedirecting}
                  onChange={(e) => setPassword(e.target.value)}
                  required={!email.toLowerCase().endsWith("@nabi.health")}
                />
                {!isSignUpMode && (
                  <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '15px' }}>
                    <span 
                      onClick={handleForgotPassword}
                      style={{ color: '#003049', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Forgot Password?
                    </span>
                  </div>
                )}
                <button type="submit" className="navbar-modal-submit" style={{ opacity: isAutoRedirecting ? 0.5 : 1 }} disabled={isAutoRedirecting}>
                  {isSignUpMode ? "Create Account" : "Log In"}
                </button>
              </form>
              <div className="navbar-modal-divider"><span>or</span></div>
              <div className="navbar-modal-social-btn" onClick={() => handleSocialLogin(googleProvider)}>
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="navbar-modal-social-icon" referrerPolicy="no-referrer" />
                Continue with Google
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* NAVBAR */}
      <nav className="navbar">
        <div className="navbar-logo" onClick={() => navigate("/")}>
          <img src="/Nabi UI Assets/Assets_Images/Logo placeholder.png" alt="Nabi" className="navbar-cornerlogo" />
          World of Nabi
        </div>

        <div className={`navbar-links ${isMobileMenuOpen ? 'mobile-active' : ''}`}>
          <span onClick={() => { navigate("/about"); setIsMobileMenuOpen(false); }} className="navbar-item">About</span>
          <span onClick={() => { navigate("/nabi-health"); setIsMobileMenuOpen(false); }} className="navbar-item">Nabi Health</span>
          <span onClick={() => { navigate("/store"); setIsMobileMenuOpen(false); }} className="navbar-item">Nabi Store</span>
          <span onClick={() => { navigate("/training"); setIsMobileMenuOpen(false); }} className="navbar-item">Nabi Training</span>
         
          <span onClick={() => { navigate("/blog"); setIsMobileMenuOpen(false); }} className="navbar-item">Nabi Stories</span>
         <span onClick={() => { navigate("/events"); setIsMobileMenuOpen(false); }} className="navbar-item">Nabi Events</span>
        </div>

        <div className="navbar-right">
          {/* Login/User Menu */}
          <div className="navbar-login-container" onClick={() => user ? setIsMenuOpen(!isMenuOpen) : setIsLoginOpen(true)}>
            <div className="navbar-user-icon">
              {user ? (
                user.photoURL ?
                  <img src={user.photoURL} alt="User" className="navbar-avatar" /> :
                  <span>{user.email?.[0]?.toUpperCase() || 'U'}</span>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              )}
            </div>
            <span className="navbar-login-text">
              {user ? (user.displayName || user.name || user.email?.split('@')[0]) : "Log In"}
            </span>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {isMenuOpen && user && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="navbar-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="navbar-menu-item"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsMobileMenuOpen(false);
                      navigate("/profile");
                    }}
                  >
                    My Profile
                  </div>

                  {isAdmin && (
                    <>
                      <hr style={{ border: '0.5px solid #eee', margin: '5px 0' }} />
                      <div
                        className="navbar-menu-item"
                        style={{ color: '#003049', fontWeight: '500' }}
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsMobileMenuOpen(false);
                          navigate("/admin-dashboard");
                        }}
                      >
                        Admin Dashboard
                      </div>
                    </>
                  )}

                  <hr style={{ border: '0.5px solid #eee', margin: '5px 0' }} />
                  <div
                    className="navbar-menu-item"
                    style={{ color: '#F07167' }}
                    onClick={(e) => {
                      handleLogoutUser(e);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Log Out
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Cart Icon */}
          <div className="navbar-cart-btn" onClick={() => setIsCartOpen && setIsCartOpen(true)}>
            <img src="/Nabi UI Assets/Assets_Icon/Icon_Cart.svg" alt="cart" className="navbar-cart-icon" />
            {globalCart && globalCart.length > 0 && (
              <span className="navbar-badge">{globalCart.length}</span>
            )}
          </div>

          {/* Hamburger Menu Toggle */}
          <div className={`navbar-hamburger ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>
    </>
  );
}


